import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import axiosInstance from '@/lib/axios';
type ViewType =
  | 'chat'
  | 'pdf-list'
  | 'login'
  | 'project-detail'
  | 'project-members' // 🔥 新增：项目成员管理
  | 'agents-list'
  | 'chats-list'
  | 'projects-list'
  | 'shared-list'
  | 'user-settings'; // 🔥 新增：用户设置页面
interface PDFViewerState {
  url: string;
  title: string;
  page?: number;
}

interface ArtifactViewerState {
  type:
    | 'html'
    | 'jsx'
    | 'react'
    | 'text'
    | 'markdown'
    | 'mermaid'
    | 'svg'
    | 'code'
    | 'isolated-html';
  content: string;
  title: string;
  language?: string; // For code blocks
}

// 🔥 新增：文件预览状态（单例模式，用于 conversation 中的文件预览）
interface FilePreviewState {
  fileUrl: string;
  filename: string;
  projectId?: string;
  filePath?: string;
  clientId?: string; // 🔥 新增：用于通过 client 接口保存文件
}

interface Agent {
  id: string;
  name: string;
  logo?: string;
  description?: string;
  isDefault?: boolean;
}

// 任务状态类型
type TaskStatus =
  | 'waiting'
  | 'pending'
  | 'sent'
  | 'initialized'
  | 'streaming'
  | 'error'
  | 'cancelled'; // 🔥 新增：任务被取消

// 消息 UI 状态类型（用于显示）
export type MessageStatus =
  | 'waiting'
  | 'pending'
  | 'ongoing'
  | 'error'
  | undefined;

// 🔥 队列配置
const MAX_QUEUE_SIZE = 10; // 最大队列长度

// 🔥 新增：每个 conversation 最新的 message_init 信息
interface LatestMessageInit {
  conversationId: string;
  userMessageId: number;
  assistantMessageId: number;
  pendingId: string;
}

// 任务类型
type TaskType = 'chat' | 'cancel';

// 任务队列中的任务（统一 chat 和 cancel 任务）
interface TaskQueueItem {
  id: string; // 唯一标识符（pendingId）
  type: TaskType; // 🔥 新增：任务类型（chat 或 cancel）
  conversationId: string; // 目标会话ID
  createdAt: Date; // 创建时间
  status: TaskStatus; // 任务状态
  previousMessageId?: number; // 上一个任务 message_init 返回的 assistantMessageId（发送时使用）
  assistantMessageId?: number; // 本任务的 assistantMessageId（message_init 返回后填充）
  userMessageId?: number; // 本任务的 userMessageId（message_init 返回后填充）
  errorMessage?: string; // 错误信息（status 为 'error' 时填充）

  // === chat 任务特有字段 ===
  content?: string; // 消息内容（chat 任务必须）
  projectId?: string; // 项目ID
  agentId?: string | null; // Agent ID
  options?: Record<string, any>; // 附加选项

  // === cancel 任务特有字段 ===
  targetUserMessageId?: number; // 要取消的消息的 userMessageId（有 backendId 时使用）
  targetPendingId?: string; // 要取消的消息的 pendingId（还没收到 message_init 时使用）
  cancelNodeId?: number; // cancel API 返回的 messageId（用于后续任务的 previousMessageId）
}

// 兼容旧的 PendingTask 类型（用于 UI 显示）
interface PendingTask {
  id: string;
  content: string;
  conversationId: string;
  projectId?: string;
  agentId?: string | null;
  options?: Record<string, any>;
  createdAt: Date;
  previousMessageId?: number;
  errorMessage?: string; // 错误信息（status 为 'error' 时填充）
}
interface SelectedAgent {
  id: string;
  name: string;
  logo?: string;
  description?: string;
  config?: any; // Agent configuration from API
}
interface UIState {
  // State
  currentView: ViewType;
  isSidebarCollapsed: boolean;
  sidebarCollapsedBeforeArtifact: boolean | null; // Track sidebar state before opening artifact
  selectedNavItem: string;
  shareConversationId: string;
  showNewProjectModal: boolean;
  pdfViewer: PDFViewerState | null;
  artifactViewer: ArtifactViewerState | null;
  filePreview: FilePreviewState | null; // 🔥 新增：文件预览状态（单例模式）
  selectedAgent: SelectedAgent | null;
  newChatAgent: SelectedAgent | null; // Agent selected for new chat (preserved across conversation switches)
  newChatProject: { id: string; name: string } | null; // Project selected for new chat (preserved across conversation switches)
  agentConfigValues: Record<string, any>; // Agent form control values
  agents: Agent[]; // Global agents list
  agentsLoaded: boolean; // Track if agents have been fetched
  defaultAgent: Agent | null; // Default agent from API (isDefault=true)
  agentsFetching: boolean; // Prevent duplicate fetch calls
  conversationAgentMap: Record<string, Agent>; // Map of conversationId -> manually selected agent
  // 🔥 新增：输入框草稿存储（用于刷新页面时保留用户输入）
  draftMessages: Record<string, string>; // Map of conversationId -> draft message content
  // 任务队列状态
  taskQueue: TaskQueueItem[]; // 任务队列（包含所有状态的任务）
  isSendingTask: boolean; // 是否正在发送任务（防止并发发送）
  // 兼容旧的 pendingTasks（用于 UI 显示，只包含 pending 和 sent 状态的任务）
  pendingTasks: PendingTask[]; // 等待中的任务队列（最多5个）
  isStreamingActive: boolean; // 当前是否有消息正在流式输出
  currentStreamingAssistantMessageId: number | null; // 当前正在流式输出的消息的 assistantMessageId（来自 message_init）
  isWaitingForMessageInit: boolean; // 🔥 新增：是否正在等待 message_init 事件（用于禁用发送按钮）
  sentFromNewChat: boolean; // 🔥 新增：标记从新对话发送消息（用于防止草稿恢复覆盖清空的输入框）
  // 🔥 新增：每个 conversation 最新的 message_init 信息（用于 Cancel 操作）
  latestMessageInitMap: Record<string, LatestMessageInit>;
  // 🔥 新增：每个 conversation 的活跃流式消息 Map（用于控制 Stop 按钮显示）
  activeStreamingMessages: Record<string, string[]>; // conversationId -> pendingId[]
  // 🔥 新增：活跃 skill 会话追踪（使用群聊 API 但不标记为群聊）
  activeSkillConversations: Record<string, boolean>; // conversationId -> true
  // Mobile fullscreen mode
  isMobileFullscreen: boolean;
  setMobileFullscreen: (fullscreen: boolean) => void;
  toggleMobileFullscreen: () => void;
  // Checkbox states
  generalUsePublic: boolean;
  generalAllProjects: boolean;
  projectUsePublic: Record<string, boolean>;
  projectAllProjects: Record<string, boolean>;
  // Projects list tab
  projectsListInitialTab: 'active' | 'archived';
  // Chats list tab
  chatsListInitialTab: 'chats' | 'groupChats';
  // Actions
  setCurrentView: (view: ViewType) => void;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  setSelectedNavItem: (item: string) => void;
  setShareConversationId: (id: string) => void;
  setShowNewProjectModal: (show: boolean) => void;
  setPdfViewer: (viewer: PDFViewerState | null) => void;
  setArtifactViewer: (viewer: ArtifactViewerState | null) => void;
  setFilePreview: (viewer: FilePreviewState | null) => void; // 🔥 新增：设置文件预览
  setSelectedAgent: (agent: SelectedAgent | null) => void;
  setNewChatAgent: (agent: SelectedAgent | null) => void;
  setNewChatProject: (project: { id: string; name: string } | null) => void;
  setAgentConfigValues: (values: Record<string, any>) => void;
  setAgents: (agents: Agent[]) => void;
  setAgentsLoaded: (loaded: boolean) => void;
  setDefaultAgent: (agent: Agent | null) => void;
  fetchAgents: () => Promise<void>; // Centralized fetch agents function
  setConversationAgent: (conversationId: string, agent: Agent) => void; // Set agent for a conversation
  clearConversationAgent: (conversationId: string) => void; // Clear agent for a conversation
  setGeneralUsePublic: (value: boolean) => void;
  setGeneralAllProjects: (value: boolean) => void;
  setProjectUsePublic: (projectId: string, value: boolean) => void;
  setProjectAllProjects: (projectId: string, value: boolean) => void;
  clearCheckboxStates: () => void;
  setProjectsListInitialTab: (tab: 'active' | 'archived') => void;
  setChatsListInitialTab: (tab: 'chats' | 'groupChats') => void;
  // 任务队列操作
  addTaskToQueue: (
    task: Omit<TaskQueueItem, 'id' | 'createdAt' | 'status'> & { id?: string }
  ) => boolean; // 添加任务到队列
  updateTaskStatus: (
    taskId: string,
    status: TaskStatus,
    assistantMessageId?: number,
    userMessageId?: number, // 🔥 新增：存储 userMessageId
    errorMessage?: string
  ) => void; // 更新任务状态
  removeTask: (taskId: string) => void; // 移除任务
  getNextPendingTask: () => TaskQueueItem | null; // 获取下一个待发送的任务
  getTaskById: (taskId: string) => TaskQueueItem | undefined; // 根据 ID 获取任务
  getTasksByStatus: (status: TaskStatus) => TaskQueueItem[]; // 根据状态获取任务列表
  setIsSendingTask: (isSending: boolean) => void; // 设置是否正在发送
  canSendNextTask: () => boolean; // 检查是否可以发送下一个任务
  getLastAssistantMessageId: (conversationId?: string) => number | null; // 获取指定会话最后一个已初始化任务的 assistantMessageId
  clearTaskQueue: () => void; // 清空任务队列
  // 兼容旧的方法
  addPendingTask: (
    task: Omit<PendingTask, 'id' | 'createdAt'> & { id?: string }
  ) => boolean;
  removePendingTask: (taskId: string) => void;
  clearPendingTasks: () => void;
  getWaitingTasksCount: () => number; // 🔥 新增：获取等待中的任务数量
  clearWaitingTasks: () => void; // 🔥 新增：清空所有等待中的任务（错误恢复时使用）
  setStreamingActive: (active: boolean) => void;
  canAddPendingTask: () => boolean;
  setCurrentStreamingAssistantMessageId: (id: number | null) => void;
  getCurrentStreamingAssistantMessageId: () => number | null;
  setIsWaitingForMessageInit: (waiting: boolean) => void; // 🔥 新增：设置是否正在等待 message_init
  getIsWaitingForMessageInit: () => boolean; // 🔥 新增：获取是否正在等待 message_init
  // 🔥 新增：草稿存储方法（用于刷新页面时保留用户输入）
  setDraftMessage: (conversationId: string, content: string) => void;
  getDraftMessage: (conversationId: string) => string;
  clearDraftMessage: (conversationId: string) => void;
  // 🔥 新增：标记从新对话发送消息（用于防止草稿恢复覆盖清空的输入框）
  setSentFromNewChat: (value: boolean) => void;
  getSentFromNewChat: () => boolean;
  // 🔥 新增：插入 cancel 任务到队列最前面
  insertCancelTaskToFront: (task: {
    conversationId: string;
    targetUserMessageId?: number; // 有 backendId 时使用
    targetPendingId?: string; // 还没收到 message_init 时使用
    previousMessageId?: number;
  }) => void;
  // 🔥 新增：更新 cancel 任务的目标 ID（当 message_init 返回时调用）
  updateCancelTaskTargetIds: (
    targetPendingId: string,
    targetUserMessageId: number,
    previousMessageId?: number
  ) => void;
  // 🔥 新增：最新 message_init 信息管理
  setLatestMessageInit: (data: LatestMessageInit) => void;
  getLatestMessageInit: (
    conversationId: string
  ) => LatestMessageInit | undefined;
  clearLatestMessageInit: (conversationId: string) => void;
  // 🔥 新增：活跃流式消息管理（用于控制 Stop 按钮显示）
  addActiveStreamingMessage: (
    conversationId: string,
    pendingId: string
  ) => void;
  removeActiveStreamingMessage: (
    conversationId: string,
    pendingId: string
  ) => void;
  hasActiveStreamingMessages: (conversationId: string) => boolean;
  clearActiveStreamingMessages: (conversationId: string) => void;
  // 🔥 新增：skill 会话管理（使用群聊 WebSocket 但不标记为群聊）
  addSkillConversation: (conversationId: string) => void;
  removeSkillConversation: (conversationId: string) => void;
  isSkillConversation: (conversationId: string) => boolean;
}
export const useUIStore = create<UIState>()(
  devtools(
    (set, get) => ({
      // Initial state
      // 检测 OAuth 重定向，如果 URL 包含 code 参数，默认显示登录页
      currentView: (() => {
        // 只在浏览器环境中执行
        if (typeof window !== 'undefined') {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.has('code')) {
            return 'login';
          }
        }
        return 'chat';
      })(),
      isSidebarCollapsed: false,
      sidebarCollapsedBeforeArtifact: null,
      selectedNavItem: '',
      shareConversationId: '',
      showNewProjectModal: false,
      pdfViewer: null,
      artifactViewer: null,
      filePreview: null, // 🔥 新增：文件预览初始状态
      selectedAgent: null,
      newChatAgent: null,
      newChatProject: null,
      agentConfigValues: {},
      agents: [],
      agentsLoaded: false,
      defaultAgent: null,
      agentsFetching: false,
      conversationAgentMap: {},
      // 🔥 新增：输入框草稿存储
      draftMessages: {},
      // 任务队列初始状态
      taskQueue: [],
      isSendingTask: false,
      pendingTasks: [], // 兼容旧的（计算属性）
      isStreamingActive: false,
      currentStreamingAssistantMessageId: null,
      isWaitingForMessageInit: false, // 🔥 新增：初始不在等待状态
      sentFromNewChat: false, // 🔥 新增：标记从新对话发送消息
      latestMessageInitMap: {}, // 🔥 新增：每个 conversation 最新的 message_init 信息
      activeStreamingMessages: {}, // 🔥 新增：每个 conversation 的活跃流式消息
      activeSkillConversations: {}, // 🔥 活跃 skill 会话追踪（纯内存状态，刷新后由 fetchBoard 按需恢复）
      isMobileFullscreen: false,
      generalUsePublic: true,
      generalAllProjects: true,
      projectUsePublic: {},
      projectAllProjects: {},
      projectsListInitialTab: 'active',
      chatsListInitialTab: 'chats',
      // Actions
      setCurrentView: view => set({ currentView: view }),
      setIsSidebarCollapsed: collapsed =>
        set({ isSidebarCollapsed: collapsed }),
      toggleSidebar: () =>
        set(state => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
      setSelectedNavItem: item => set({ selectedNavItem: item }),
      setShareConversationId: id => set({ shareConversationId: id }),
      setShowNewProjectModal: show => set({ showNewProjectModal: show }),
      setPdfViewer: viewer => set({ pdfViewer: viewer }),
      setArtifactViewer: viewer => set({ artifactViewer: viewer }),
      setFilePreview: viewer => set({ filePreview: viewer }), // 🔥 新增：设置文件预览
      setSelectedAgent: agent => set({ selectedAgent: agent }),
      setNewChatAgent: agent => set({ newChatAgent: agent }),
      setNewChatProject: project => set({ newChatProject: project }),
      setAgentConfigValues: values => set({ agentConfigValues: values }),
      setAgents: agents => set({ agents }),
      setAgentsLoaded: loaded => set({ agentsLoaded: loaded }),
      setDefaultAgent: agent => set({ defaultAgent: agent }),
      fetchAgents: async () => {
        const state = useUIStore.getState();
        // Prevent duplicate calls
        if (state.agentsLoaded || state.agentsFetching) {
          return;
        }

        // 检查是否已登录，未登录则不执行
        // const token = localStorage.getItem('user-token');
        // const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
        // if (!token || !isLoggedIn) {
        //   console.log('fetchAgents: User not logged in, skipping fetch');
        //   return;
        // }

        set({ agentsFetching: true });
        try {
          const response = await axiosInstance.get('/api/clients');
          const data = response.data;
          const clients = data.clients || [];
          // Find the default agent (isDefault = true)
          const defaultAgent =
            clients.find((agent: Agent) => agent.isDefault) || null;
          set({
            agents: clients,
            agentsLoaded: true,
            agentsFetching: false,
            defaultAgent,
          });
        } catch (error) {
          // axios 拦截器会自动处理 401 错误并跳转到登录页
          // 其他错误也会显示提示，这里只需要重置状态
          console.error('Failed to fetch agents:', error);
          set({ agentsFetching: false });
        }
      },
      setConversationAgent: (conversationId, agent) =>
        set(state => ({
          conversationAgentMap: {
            ...state.conversationAgentMap,
            [conversationId]: agent,
          },
        })),
      clearConversationAgent: conversationId =>
        set(state => {
          const newMap = { ...state.conversationAgentMap };
          delete newMap[conversationId];
          return { conversationAgentMap: newMap };
        }),
      setGeneralUsePublic: value => set({ generalUsePublic: value }),
      setGeneralAllProjects: value => set({ generalAllProjects: value }),
      setProjectUsePublic: (projectId, value) =>
        set(state => ({
          projectUsePublic: { ...state.projectUsePublic, [projectId]: value },
        })),
      setProjectAllProjects: (projectId, value) =>
        set(state => ({
          projectAllProjects: {
            ...state.projectAllProjects,
            [projectId]: value,
          },
        })),
      clearCheckboxStates: () =>
        set({
          generalUsePublic: true,
          generalAllProjects: true,
          projectUsePublic: {},
          projectAllProjects: {},
        }),
      setProjectsListInitialTab: tab => set({ projectsListInitialTab: tab }),
      setChatsListInitialTab: tab => set({ chatsListInitialTab: tab }),
      setMobileFullscreen: fullscreen =>
        set({ isMobileFullscreen: fullscreen }),
      toggleMobileFullscreen: () =>
        set(state => ({ isMobileFullscreen: !state.isMobileFullscreen })),
      // 任务队列操作
      addTaskToQueue: task => {
        const { taskQueue } = get();
        // 只计算 pending 和 sent 状态的任务
        const waitingTasks = taskQueue.filter(
          t => t.status === 'pending' || t.status === 'sent'
        );
        if (waitingTasks.length >= MAX_QUEUE_SIZE) {
          return false;
        }
        const newTask: TaskQueueItem = {
          id:
            task.id ||
            `task-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          type: 'chat', // 🔥 新增：标记为 chat 任务
          content: task.content,
          conversationId: task.conversationId,
          projectId: task.projectId,
          agentId: task.agentId,
          options: task.options,
          previousMessageId: task.previousMessageId,
          createdAt: new Date(),
          status: 'pending',
        };
        set(state => {
          const newTaskQueue = [...state.taskQueue, newTask];
          // 更新兼容的 pendingTasks
          // 🔥 修改：包含 initialized 状态，让任务在 message_start 之前一直显示在等待列表中
          const pendingTasks = newTaskQueue
            .filter(
              t =>
                t.status === 'pending' ||
                t.status === 'sent' ||
                t.status === 'initialized'
            )
            .map(t => ({
              id: t.id,
              content: t.content || '',
              conversationId: t.conversationId,
              projectId: t.projectId,
              agentId: t.agentId,
              options: t.options,
              createdAt: t.createdAt,
              previousMessageId: t.previousMessageId,
            }));
          return { taskQueue: newTaskQueue, pendingTasks };
        });
        return true;
      },
      updateTaskStatus: (
        taskId,
        status,
        assistantMessageId,
        userMessageId,
        errorMessage
      ) =>
        set(state => {
          const newTaskQueue = state.taskQueue.map(task =>
            task.id === taskId
              ? {
                  ...task,
                  status,
                  ...(assistantMessageId !== undefined
                    ? { assistantMessageId }
                    : {}),
                  ...(userMessageId !== undefined ? { userMessageId } : {}), // 🔥 新增：存储 userMessageId
                  ...(errorMessage !== undefined ? { errorMessage } : {}),
                }
              : task
          );
          // 更新兼容的 pendingTasks
          // 🔥 修改：包含 initialized 和 error 状态
          const pendingTasks = newTaskQueue
            .filter(
              t =>
                t.status === 'pending' ||
                t.status === 'sent' ||
                t.status === 'initialized' ||
                t.status === 'error'
            )
            .map(t => ({
              id: t.id,
              content: t.content || '',
              conversationId: t.conversationId,
              projectId: t.projectId,
              agentId: t.agentId,
              options: t.options,
              createdAt: t.createdAt,
              previousMessageId: t.previousMessageId,
              errorMessage: t.errorMessage,
            }));
          return { taskQueue: newTaskQueue, pendingTasks };
        }),
      removeTask: taskId =>
        set(state => {
          const newTaskQueue = state.taskQueue.filter(
            task => task.id !== taskId
          );
          // 🔥 修改：包含 initialized 状态，让任务在 message_start 之前一直显示在等待列表中
          const pendingTasks = newTaskQueue
            .filter(
              t =>
                t.status === 'pending' ||
                t.status === 'sent' ||
                t.status === 'initialized'
            )
            .map(t => ({
              id: t.id,
              content: t.content || '',
              conversationId: t.conversationId,
              projectId: t.projectId,
              agentId: t.agentId,
              options: t.options,
              createdAt: t.createdAt,
              previousMessageId: t.previousMessageId,
            }));
          return { taskQueue: newTaskQueue, pendingTasks };
        }),
      getNextPendingTask: () => {
        const { taskQueue } = get();
        return taskQueue.find(t => t.status === 'pending') || null;
      },
      getTaskById: taskId => {
        return get().taskQueue.find(t => t.id === taskId);
      },
      getTasksByStatus: status => {
        return get().taskQueue.filter(t => t.status === status);
      },
      setIsSendingTask: isSending => set({ isSendingTask: isSending }),
      canSendNextTask: () => {
        const { taskQueue, isSendingTask } = get();
        // 如果正在发送，不能发送下一个
        if (isSendingTask) return false;
        // 如果有 sent 状态的任务（还没收到 message_init），不能发送下一个
        const sentTasks = taskQueue.filter(t => t.status === 'sent');
        if (sentTasks.length > 0) return false;
        return true;
      },
      getLastAssistantMessageId: (conversationId?: string) => {
        const { taskQueue, latestMessageInitMap } = get();

        // 🔥 修复：优先从 latestMessageInit 中获取值
        // 这确保 Cancel 完成后更新的 assistantMessageId 能被正确使用
        if (conversationId && latestMessageInitMap[conversationId]) {
          const latestInit = latestMessageInitMap[conversationId];
          if (latestInit.assistantMessageId) {
            return latestInit.assistantMessageId;
          }
        }

        // 回退：从后往前找最后一个有 assistantMessageId 的任务
        // 如果指定了 conversationId，只查找该会话的任务，避免跨会话串台
        for (let i = taskQueue.length - 1; i >= 0; i--) {
          const task = taskQueue[i];
          if (task.assistantMessageId) {
            // 如果指定了 conversationId，必须匹配
            if (conversationId && task.conversationId !== conversationId) {
              continue;
            }
            return task.assistantMessageId!;
          }
        }
        return null;
      },
      clearTaskQueue: () =>
        set({ taskQueue: [], pendingTasks: [], isSendingTask: false }),
      // 兼容旧的方法
      addPendingTask: task => {
        const { taskQueue } = get();
        // 🔥 修改：包含 initialized 状态
        const waitingTasks = taskQueue.filter(
          t =>
            t.status === 'pending' ||
            t.status === 'sent' ||
            t.status === 'initialized'
        );
        if (waitingTasks.length >= MAX_QUEUE_SIZE) {
          return false;
        }
        const newTask: TaskQueueItem = {
          id:
            task.id ||
            `task-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          type: 'chat', // 🔥 新增：标记为 chat 任务
          content: task.content,
          conversationId: task.conversationId,
          projectId: task.projectId,
          agentId: task.agentId,
          options: task.options,
          previousMessageId: task.previousMessageId,
          createdAt: new Date(),
          status: 'pending',
        };
        set(state => {
          const newTaskQueue = [...state.taskQueue, newTask];
          // 🔥 修改：包含 initialized 状态，让任务在 message_start 之前一直显示在等待列表中
          const pendingTasks = newTaskQueue
            .filter(
              t =>
                t.status === 'pending' ||
                t.status === 'sent' ||
                t.status === 'initialized'
            )
            .map(t => ({
              id: t.id,
              content: t.content || '',
              conversationId: t.conversationId,
              projectId: t.projectId,
              agentId: t.agentId,
              options: t.options,
              createdAt: t.createdAt,
              previousMessageId: t.previousMessageId,
            }));
          return { taskQueue: newTaskQueue, pendingTasks };
        });
        return true;
      },
      removePendingTask: taskId =>
        set(state => {
          const newTaskQueue = state.taskQueue.filter(
            task => task.id !== taskId
          );
          // 🔥 修改：包含 initialized 状态，让任务在 message_start 之前一直显示在等待列表中
          const pendingTasks = newTaskQueue
            .filter(
              t =>
                t.status === 'pending' ||
                t.status === 'sent' ||
                t.status === 'initialized'
            )
            .map(t => ({
              id: t.id,
              content: t.content || '',
              conversationId: t.conversationId,
              projectId: t.projectId,
              agentId: t.agentId,
              options: t.options,
              createdAt: t.createdAt,
              previousMessageId: t.previousMessageId,
            }));
          return { taskQueue: newTaskQueue, pendingTasks };
        }),
      clearPendingTasks: () =>
        set({ taskQueue: [], pendingTasks: [], isSendingTask: false }),
      setStreamingActive: active => set({ isStreamingActive: active }),
      canAddPendingTask: () => {
        const { taskQueue } = get();
        // 🔥 修改：包含 initialized 状态
        const waitingTasks = taskQueue.filter(
          t =>
            t.status === 'pending' ||
            t.status === 'sent' ||
            t.status === 'initialized'
        );
        return waitingTasks.length < MAX_QUEUE_SIZE;
      },
      // 🔥 新增：获取等待中的任务数量（用于判断是否禁用发送按钮）
      getWaitingTasksCount: () => {
        const { taskQueue } = get();
        return taskQueue.filter(
          t =>
            t.status === 'pending' ||
            t.status === 'sent' ||
            t.status === 'initialized'
        ).length;
      },
      // 🔥 新增：清空所有等待中的任务（错误恢复时使用）
      clearWaitingTasks: () => {
        set(state => {
          // 只保留 streaming 和 error 状态的任务
          const newTaskQueue = state.taskQueue.filter(
            t => t.status === 'streaming' || t.status === 'error'
          );
          const pendingTasks = newTaskQueue
            .filter(t => t.status === 'error')
            .map(t => ({
              id: t.id,
              content: t.content || '',
              conversationId: t.conversationId,
              projectId: t.projectId,
              agentId: t.agentId,
              options: t.options,
              createdAt: t.createdAt,
              previousMessageId: t.previousMessageId,
              errorMessage: t.errorMessage,
            }));
          return { taskQueue: newTaskQueue, pendingTasks };
        });
      },
      setCurrentStreamingAssistantMessageId: id =>
        set({ currentStreamingAssistantMessageId: id }),
      getCurrentStreamingAssistantMessageId: () =>
        get().currentStreamingAssistantMessageId,
      // 🔥 新增：设置是否正在等待 message_init
      setIsWaitingForMessageInit: waiting =>
        set({ isWaitingForMessageInit: waiting }),
      getIsWaitingForMessageInit: () => get().isWaitingForMessageInit,
      // 🔥 新增：标记从新对话发送消息
      setSentFromNewChat: value => set({ sentFromNewChat: value }),
      getSentFromNewChat: () => get().sentFromNewChat,
      // 🔥 新增：草稿存储方法（用于刷新页面时保留用户输入）
      setDraftMessage: (conversationId, content) =>
        set(state => ({
          draftMessages: {
            ...state.draftMessages,
            [conversationId]: content,
          },
        })),
      getDraftMessage: conversationId => {
        const { draftMessages } = get();
        return draftMessages[conversationId] || '';
      },
      clearDraftMessage: conversationId =>
        set(state => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [conversationId]: _removed, ...rest } = state.draftMessages;
          return { draftMessages: rest };
        }),
      // 🔥 新增：插入 cancel 任务到队列最前面
      insertCancelTaskToFront: task =>
        set(state => {
          const cancelTask: TaskQueueItem = {
            id: `cancel-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            type: 'cancel',
            conversationId: task.conversationId,
            createdAt: new Date(),
            status: 'pending', // 🔥 使用 'pending' 状态，以便被 getNextPendingTask 选中
            targetUserMessageId: task.targetUserMessageId,
            targetPendingId: task.targetPendingId, // 🔥 用于还没收到 message_init 的情况
            previousMessageId: task.previousMessageId,
          };

          // 🔥 插入到队列最前面（在所有 waiting 任务之前）
          const newTaskQueue = [cancelTask, ...state.taskQueue];

          // 更新 pendingTasks（用于 UI 显示）
          const pendingTasks = newTaskQueue
            .filter(t => t.status === 'waiting' || t.status === 'pending')
            .map(t => ({
              id: t.id,
              content: t.content || '',
              conversationId: t.conversationId,
              projectId: t.projectId,
              agentId: t.agentId,
              options: t.options,
              createdAt: t.createdAt,
              previousMessageId: t.previousMessageId,
            }));

          console.log('📍 插入 cancel 任务到队列最前面', {
            cancelTask,
            queueLength: newTaskQueue.length,
          });

          return { taskQueue: newTaskQueue, pendingTasks };
        }),
      // 🔥 新增：更新 cancel 任务的目标 ID（当 message_init 返回时调用）
      updateCancelTaskTargetIds: (
        targetPendingId,
        targetUserMessageId,
        previousMessageId
      ) =>
        set(state => {
          const newTaskQueue = state.taskQueue.map(task => {
            if (
              task.type === 'cancel' &&
              task.targetPendingId === targetPendingId
            ) {
              return {
                ...task,
                targetUserMessageId,
                previousMessageId: previousMessageId ?? task.previousMessageId,
              };
            }
            return task;
          });

          console.log('📍 更新 cancel 任务的目标 ID', {
            targetPendingId,
            targetUserMessageId,
            previousMessageId,
          });

          return { taskQueue: newTaskQueue };
        }),
      // 🔥 新增：最新 message_init 信息管理
      setLatestMessageInit: data =>
        set(state => ({
          latestMessageInitMap: {
            ...state.latestMessageInitMap,
            [data.conversationId]: data,
          },
        })),
      getLatestMessageInit: conversationId => {
        const { latestMessageInitMap } = get();
        return latestMessageInitMap[conversationId];
      },
      clearLatestMessageInit: conversationId =>
        set(state => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [conversationId]: _, ...rest } = state.latestMessageInitMap;
          return { latestMessageInitMap: rest };
        }),
      // 🔥 新增：活跃流式消息管理（用于控制 Stop 按钮显示）
      addActiveStreamingMessage: (conversationId, pendingId) =>
        set(state => {
          const current = state.activeStreamingMessages[conversationId] || [];
          if (current.includes(pendingId)) {
            return state; // Already exists
          }
          console.log('📥 addActiveStreamingMessage:', {
            conversationId,
            pendingId,
            currentCount: current.length + 1,
          });
          return {
            activeStreamingMessages: {
              ...state.activeStreamingMessages,
              [conversationId]: [...current, pendingId],
            },
          };
        }),
      removeActiveStreamingMessage: (conversationId, pendingId) =>
        set(state => {
          const current = state.activeStreamingMessages[conversationId] || [];
          const updated = current.filter(id => id !== pendingId);
          if (updated.length === current.length) {
            return state; // Not found
          }
          console.log('📤 removeActiveStreamingMessage:', {
            conversationId,
            pendingId,
            remainingCount: updated.length,
          });
          return {
            activeStreamingMessages: {
              ...state.activeStreamingMessages,
              [conversationId]: updated,
            },
          };
        }),
      hasActiveStreamingMessages: conversationId => {
        const { activeStreamingMessages } = get();
        const messages = activeStreamingMessages[conversationId] || [];
        return messages.length > 0;
      },
      clearActiveStreamingMessages: conversationId =>
        set(state => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [conversationId]: _, ...rest } =
            state.activeStreamingMessages;
          return { activeStreamingMessages: rest };
        }),
      // 🔥 skill 会话管理（纯内存状态，不持久化到 localStorage）
      // 刷新浏览器后通过 fetchBoard 按需恢复
      addSkillConversation: conversationId =>
        set(state => ({
          activeSkillConversations: {
            ...state.activeSkillConversations,
            [conversationId]: true,
          },
        })),
      removeSkillConversation: conversationId =>
        set(state => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [conversationId]: _, ...rest } =
            state.activeSkillConversations;
          return { activeSkillConversations: rest };
        }),
      isSkillConversation: conversationId => {
        return !!get().activeSkillConversations[conversationId];
      },
    }),
    { name: 'UIStore' }
  )
);
